import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowForward,
  AccessTime,
  BusinessCenter,
  Email,
  Check,
  Warning,
  Info,
} from '@mui/icons-material';

import { Offer } from '../models/Offer';

type Props = {
  offer: Offer;
};

function OfferCard({ offer }: Props) {
  const isValid = new Date(offer.validUntil).getTime() > Date.now();

  // Formatowanie daty
  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Obliczenie liczby różnych produktów i łącznej ilości
  const totalProductTypes = offer.offerProducts?.length || 0;
  const totalItems =
    offer.offerProducts?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Obliczenie pozostałego czasu ważności oferty
  const getRemainingDays = () => {
    const today = new Date();
    const validUntil = new Date(offer.validUntil);
    const diffTime = validUntil.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const remainingDays = getRemainingDays();

  // Status ikony i kolory
  const getStatusIcon = () => {
    if (!isValid) return <Warning fontSize="small" />;
    if (remainingDays <= 3) return <Info fontSize="small" />;
    return <Check fontSize="small" />;
  };

  const getStatusColor = () => {
    if (!isValid) return 'bg-red-50 text-red-700 border-red-200';
    if (remainingDays <= 3)
      return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-green-50 text-green-700 border-green-200';
  };

  return (
    <motion.div
      className={`bg-white rounded-lg border ${
        isValid ? 'border-gray-200' : 'border-red-200'
      } overflow-hidden hover:shadow-md transition-shadow`}
      whileHover={{ y: -2 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-medium text-gray-900 text-base">
              Oferta {offer.offerNumber}
            </h3>
            <p className="text-sm text-gray-500">{formatDate(offer.sentAt)}</p>
          </div>

          <div className="flex gap-2">
            <div
              className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 border ${getStatusColor()}`}
            >
              {getStatusIcon()}
              {isValid
                ? `Ważna do ${formatDate(offer.validUntil)}`
                : 'Oferta wygasła'}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center">
              <BusinessCenter fontSize="small" className="text-gray-400 mr-1" />
              <span className="text-sm font-medium">
                {offer.inquiry?.name || 'Brak danych klienta'}
              </span>
            </div>

            {offer.inquiry?.nip && (
              <div className="bg-gray-50 px-2 py-1 rounded text-xs font-medium text-gray-600">
                NIP: {offer.inquiry.nip}
              </div>
            )}
          </div>

          {offer.inquiry?.email && (
            <div className="flex items-center text-sm text-gray-500">
              <Email fontSize="small" className="text-gray-400 mr-1" />
              <span>{offer.inquiry.email}</span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-end mb-3">
          <div className="flex items-center">
            <div className="flex -space-x-2 mr-2">
              {offer.offerProducts?.slice(0, 3).map((item) => (
                <div
                  key={item.productId}
                  className="w-8 h-8 border-2 border-white rounded-full overflow-hidden bg-gray-100"
                >
                  {item.product?.mobilePrimaryImage ? (
                    <img
                      src={item.product.mobilePrimaryImage}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-xs text-gray-500">?</span>
                    </div>
                  )}
                </div>
              ))}

              {totalProductTypes > 3 && (
                <div className="w-8 h-8 border-2 border-white rounded-full bg-gray-50 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-600">
                    +{totalProductTypes - 3}
                  </span>
                </div>
              )}
            </div>

            <span className="text-sm text-gray-600">
              {totalItems}{' '}
              {totalItems === 1
                ? 'produkt'
                : totalItems < 5
                  ? 'produkty'
                  : 'produktów'}
            </span>
          </div>

          <div className="font-semibold text-lg">
            {offer.price.toFixed(2)} zł
          </div>
        </div>

        {offer.offerDetails && (
          <div className="text-sm text-gray-500 mb-3">
            <p className="truncate">"{offer.offerDetails}"</p>
          </div>
        )}

        <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {isValid ? (
              <p className="flex items-center">
                <AccessTime fontSize="small" className="mr-1" />
                Ważna jeszcze przez{' '}
                <span
                  className={
                    remainingDays <= 3
                      ? 'font-medium text-amber-600 ml-1'
                      : 'ml-1'
                  }
                >
                  {remainingDays} {remainingDays === 1 ? 'dzień' : 'dni'}
                </span>
              </p>
            ) : (
              <div className="flex items-center">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="py-1 px-2 text-xs font-medium text-blue-600 border border-blue-600 rounded-md flex items-center gap-1 hover:bg-blue-50 transition-colors"
                  onClick={() =>
                    (window.location.href = `/admin/oferty/odnów/${offer.id}`)
                  }
                >
                  <AccessTime fontSize="small" />
                  Odnów ofertę
                </motion.button>
              </div>
            )}
          </div>

          <Link
            to={`/admin/oferty/${offer.id}`}
            className="flex items-center text-primary font-medium hover:text-primary-dark transition-colors text-sm"
          >
            Szczegóły
            <ArrowForward fontSize="small" className="ml-1" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default OfferCard;
